/**
 * Deterministic country name → flag emoji lookup.
 * Used only during the import review UI — the final saved emoji is AI-generated at save time.
 */

/**
 * Normalise a country name so minor variations all hit the same lookup key:
 *   1. Trim + lowercase
 *   2. Strip combining diacritical marks (NFD decomposition)
 *      → "Türkiye" and "Turkiye" both become "turkiye"
 *      → "Côte d'Ivoire" and "Cote d'Ivoire" both become "cote d'ivoire"
 *   3. Strip a leading "The " / "the "
 *      → "The Bahamas", "The Gambia", "The Netherlands" all lose the article
 *   4. Normalise " & " → " and "
 *      → "Bosnia & Herzegovina" === "Bosnia and Herzegovina"
 */
function normalize(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')       // strip combining diacritical marks
    .replace(/^the\s+/, '')       // strip leading "the "
    .replace(/\s*&\s*/g, ' and '); // & → and
}

/**
 * ISO 3166-1 alpha-2 codes keyed by normalised country name.
 * All keys are already in normalised form (lowercase, no diacritics, no leading "the", & → and).
 *
 * Alias pairs for countries with two common English spellings are listed together
 * with a comment so it's easy to spot gaps.
 */
const COUNTRY_CODES: Record<string, string> = {
  // A
  afghanistan: 'AF', albania: 'AL', algeria: 'DZ', andorra: 'AD', angola: 'AO',
  'antigua and barbuda': 'AG', antigua: 'AG',
  argentina: 'AR', armenia: 'AM',
  australia: 'AU',
  austria: 'AT', osterreich: 'AT',                       // Austria / Österreich
  azerbaijan: 'AZ',
  // B
  bahamas: 'BS',                                          // "The Bahamas" → stripped to "bahamas"
  bahrain: 'BH', bangladesh: 'BD', barbados: 'BB', belarus: 'BY',
  belgium: 'BE', belgie: 'BE', belgique: 'BE',
  belize: 'BZ', benin: 'BJ', bhutan: 'BT', bolivia: 'BO',
  'bosnia and herzegovina': 'BA', 'bosnia-herzegovina': 'BA', bosnia: 'BA',
  botswana: 'BW', brazil: 'BR', brunei: 'BN', bulgaria: 'BG',
  'burkina faso': 'BF', burundi: 'BI',
  // C
  'cabo verde': 'CV', 'cape verde': 'CV',                // Cabo Verde / Cape Verde
  cambodia: 'KH', cameroon: 'CM', canada: 'CA',
  'central african republic': 'CF', chad: 'TD', chile: 'CL',
  china: 'CN', colombia: 'CO',
  comoros: 'KM',                                         // "The Comoros" → stripped
  congo: 'CG', 'republic of the congo': 'CG',
  'democratic republic of the congo': 'CD', 'dr congo': 'CD', drc: 'CD',
  'costa rica': 'CR',
  "cote d'ivoire": 'CI', 'ivory coast': 'CI',           // Côte d'Ivoire / Ivory Coast
  croatia: 'HR', cuba: 'CU', cyprus: 'CY',
  'czech republic': 'CZ', czechia: 'CZ', 'slovak republic': 'SK', // Czech Republic / Czechia
  // D
  denmark: 'DK', djibouti: 'DJ', dominica: 'DM', 'dominican republic': 'DO',
  // E
  ecuador: 'EC', egypt: 'EG', 'el salvador': 'SV', 'equatorial guinea': 'GQ',
  eritrea: 'ER', estonia: 'EE',
  eswatini: 'SZ', swaziland: 'SZ',                      // Eswatini / Swaziland
  ethiopia: 'ET',
  // F
  fiji: 'FJ', finland: 'FI', france: 'FR',
  // G
  gabon: 'GA',
  gambia: 'GM',                                          // "The Gambia" → stripped
  georgia: 'GE',
  germany: 'DE', deutschland: 'DE',                     // Germany / Deutschland
  ghana: 'GH',
  greece: 'GR', hellas: 'GR',                           // Greece / Hellas
  grenada: 'GD', guatemala: 'GT', guinea: 'GN', 'guinea-bissau': 'GW', guyana: 'GY',
  // H
  haiti: 'HT', 'holy see': 'VA', honduras: 'HN', hungary: 'HU',
  // I
  iceland: 'IS', india: 'IN', indonesia: 'ID', iran: 'IR', iraq: 'IQ',
  ireland: 'IE', 'republic of ireland': 'IE',
  israel: 'IL',
  italy: 'IT', italia: 'IT',                            // Italy / Italia
  // J
  jamaica: 'JM', japan: 'JP', jordan: 'JO',
  // K
  kazakhstan: 'KZ', kenya: 'KE', kiribati: 'KI',
  'north korea': 'KP', 'democratic peoples republic of korea': 'KP', dprk: 'KP',
  'south korea': 'KR', 'republic of korea': 'KR', korea: 'KR',
  kosovo: 'XK', 'republic of kosovo': 'XK',
  kuwait: 'KW', kyrgyzstan: 'KG', 'kyrgyz republic': 'KG',
  // L
  'lao pdr': 'LA', laos: 'LA',                          // Laos / Lao PDR
  latvia: 'LV', lebanon: 'LB', lesotho: 'LS', liberia: 'LR',
  libya: 'LY', liechtenstein: 'LI', lithuania: 'LT', luxembourg: 'LU',
  // M
  madagascar: 'MG', malawi: 'MW',
  malaysia: 'MY',
  maldives: 'MV',                                        // "The Maldives" → stripped
  mali: 'ML', malta: 'MT', 'marshall islands': 'MH',
  mauritania: 'MR', mauritius: 'MU', mexico: 'MX', micronesia: 'FM',
  moldova: 'MD', monaco: 'MC', mongolia: 'MN', montenegro: 'ME',
  morocco: 'MA', mozambique: 'MZ',
  myanmar: 'MM', burma: 'MM',                           // Myanmar / Burma
  // N
  namibia: 'NA', nauru: 'NR', nepal: 'NP',
  netherlands: 'NL', nederland: 'NL', holland: 'NL',   // Netherlands / Nederland / Holland
  'new zealand': 'NZ', nicaragua: 'NI', niger: 'NE', nigeria: 'NG',
  'north macedonia': 'MK', macedonia: 'MK',             // North Macedonia / Macedonia
  norway: 'NO',
  // O
  oman: 'OM',
  // P
  pakistan: 'PK', palau: 'PW',
  palestine: 'PS', 'palestinian territories': 'PS', 'west bank': 'PS',
  panama: 'PA', 'papua new guinea': 'PG', paraguay: 'PY', peru: 'PE',
  philippines: 'PH',                                     // "The Philippines" → stripped
  poland: 'PL', portugal: 'PT',
  // Q
  qatar: 'QA',
  // R
  romania: 'RO',
  russia: 'RU', 'russian federation': 'RU',
  rwanda: 'RW',
  // S
  'saint kitts and nevis': 'KN', 'saint lucia': 'LC',
  'saint vincent and the grenadines': 'VC',
  samoa: 'WS', 'san marino': 'SM', 'sao tome and principe': 'ST',
  'saudi arabia': 'SA', senegal: 'SN', serbia: 'RS',
  seychelles: 'SC',                                      // "The Seychelles" → stripped
  'sierra leone': 'SL', singapore: 'SG', slovakia: 'SK', slovenia: 'SI',
  'solomon islands': 'SB', somalia: 'SO', 'south africa': 'ZA', 'south sudan': 'SS',
  spain: 'ES', espana: 'ES',                            // Spain / España (normalised)
  'sri lanka': 'LK', sudan: 'SD', suriname: 'SR',
  sweden: 'SE',
  switzerland: 'CH', schweiz: 'CH', suisse: 'CH',      // Switzerland / Schweiz / Suisse
  syria: 'SY',
  // T
  taiwan: 'TW', 'republic of china': 'TW',
  tajikistan: 'TJ', tanzania: 'TZ', thailand: 'TH',
  'timor-leste': 'TL', 'east timor': 'TL',              // Timor-Leste / East Timor
  togo: 'TG', tonga: 'TO',
  'trinidad and tobago': 'TT', trinidad: 'TT',
  tunisia: 'TN',
  turkey: 'TR', turkiye: 'TR',                          // Turkey / Türkiye (diacritics stripped → turkiye)
  turkmenistan: 'TM', tuvalu: 'TV',
  // U
  uganda: 'UG', ukraine: 'UA',
  'united arab emirates': 'AE', uae: 'AE',
  'united kingdom': 'GB', uk: 'GB', 'great britain': 'GB',
  england: 'GB', scotland: 'GB', wales: 'GB',
  'united states': 'US', 'united states of america': 'US', usa: 'US', america: 'US',
  uruguay: 'UY', uzbekistan: 'UZ',
  // V
  vanuatu: 'VU', 'vatican city': 'VA', venezuela: 'VE',
  vietnam: 'VN', 'viet nam': 'VN',
  // Y
  yemen: 'YE',
  // Z
  zambia: 'ZM', zimbabwe: 'ZW',
  // Common territories / special regions
  'hong kong': 'HK', macao: 'MO', macau: 'MO',
  'puerto rico': 'PR', guam: 'GU',
  'french polynesia': 'PF', tahiti: 'PF',
  'new caledonia': 'NC',
  'faroe islands': 'FO',
  greenland: 'GL',
};

/** Convert a 2-letter ISO code to its flag emoji (regional indicator pair). */
function codeToFlag(code: string): string {
  return [...code.toUpperCase()]
    .map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65))
    .join('');
}

/**
 * Return the flag emoji for a given country name (any capitalisation, diacritics, or article).
 * Falls back to '📍' for unrecognised names.
 *
 * Examples that all resolve correctly:
 *   "Turkey" / "Türkiye" / "Turkiye"        →  🇹🇷
 *   "Myanmar" / "Burma"                      →  🇲🇲
 *   "Czech Republic" / "Czechia"             →  🇨🇿
 *   "Côte d'Ivoire" / "Ivory Coast"          →  🇨🇮
 *   "The Bahamas" / "Bahamas"                →  🇧🇸
 *   "Bosnia & Herzegovina"                   →  🇧🇦
 *   "España" / "Espana" / "Spain"            →  🇪🇸
 */
/**
 * `fallback` is what an unplaceable name comes back as. Home renders
 * "{flag} {country}", where a pin beside a country it couldn't place reads
 * worse than nothing, so that caller asks for ''.
 */
export function getCountryFlag(countryName: string, fallback = '📍'): string {
  if (!countryName?.trim()) return fallback;
  // A destination is named "Lisbon, Portugal" far more often than "Portugal",
  // and the country sits last — so read from the end, and only fall back to
  // the whole string once no part of it is a country. Reading forwards would
  // let a city that shares a country's name win over the country itself.
  const parts = countryName.split(',').map(part => part.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const code = COUNTRY_CODES[normalize(parts[i])];
    if (code) return codeToFlag(code);
  }
  const whole = COUNTRY_CODES[normalize(countryName)];
  return whole ? codeToFlag(whole) : fallback;
}

/**
 * The ISO 3166-1 alpha-2 code for a country name, lowercase — the form Google
 * Places' componentRestrictions takes — or undefined when the name isn't one.
 *
 * Same normalisation and aliases as getCountryFlag, and read off the end of a
 * comma list the same way. The Itinerary's place search used to carry its own
 * eleven-country table, left over from a demo trip, so hotel, sight and
 * restaurant search was restricted to the destination's country in France or
 * Croatia and searched the whole world everywhere else.
 */
export function countryCode(countryName: string): string | undefined {
  const parts = (countryName || '').split(',').map(part => part.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const code = COUNTRY_CODES[normalize(parts[i])];
    if (code) return code.toLowerCase();
  }
  return undefined;
}
